import { useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";

/**
 * Example smoke signals data.
 * Channels are ONLY Email or SMS.
 * Replace this with your API/DB results.
 */
const initialSignals = [
  { id: 1, sender: "Aaron Barne", channel: "Email", status: "Delivered", timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000 + 4 * 60 * 1000).toISOString(), message: "msg1" },
  { id: 2, sender: "Charlotte Gray", channel: "SMS", status: "Delivered", timestamp: new Date(Date.now() - 2.7 * 60 * 60 * 1000).toISOString(), message: "msg2" },
  { id: 3, sender: "James Whitaker", channel: "Email", status: "Failed", timestamp: new Date(Date.now() - 2.4 * 60 * 60 * 1000).toISOString(), message: "msg3" },
  { id: 4, sender: "Olivia Bennett", channel: "Email", status: "Delivered", timestamp: new Date(Date.now() - 2.2 * 60 * 60 * 1000).toISOString(), message: "msg1" },
  { id: 5, sender: "Henry Cooper", channel: "SMS", status: "Delivered", timestamp: new Date(Date.now() - 2.1 * 60 * 60 * 1000).toISOString(), message: "msg2" },
  { id: 6, sender: "Aaron Barne", channel: "Email", status: "Pending", timestamp: new Date(Date.now() - 2.0 * 60 * 60 * 1000 + 12 * 60 * 1000).toISOString(), message: "msg4" },
  { id: 7, sender: "Charlotte Gray", channel: "Email", status: "Delivered", timestamp: new Date(Date.now() - 1.8 * 60 * 60 * 1000).toISOString(), message: "msg5" },
  { id: 8, sender: "James Whitaker", channel: "SMS", status: "Delivered", timestamp: new Date(Date.now() - 1.4 * 60 * 60 * 1000).toISOString(), message: "msg2" },
  { id: 9, sender: "Olivia Bennett", channel: "SMS", status: "Failed", timestamp: new Date(Date.now() - 1.2 * 60 * 60 * 1000).toISOString(), message: "msg1" },
  { id: 10, sender: "Henry Cooper", channel: "Email", status: "Delivered", timestamp: new Date(Date.now() - 1.0 * 60 * 60 * 1000 + 5 * 60 * 1000).toISOString(), message: "msg2" },
  { id: 11, sender: "Aaron Barne", channel: "SMS", status: "Delivered", timestamp: new Date(Date.now() - 50 * 60 * 1000).toISOString(), message: "msg5" },
  { id: 12, sender: "Charlotte Gray", channel: "Email", status: "Delivered", timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(), message: "msg6" },
  { id: 13, sender: "James Whitaker", channel: "Email", status: "Pending", timestamp: new Date(Date.now() - 32 * 60 * 1000).toISOString(), message: "msg7" },
  { id: 14, sender: "Olivia Bennett", channel: "SMS", status: "Delivered", timestamp: new Date(Date.now() - 25 * 60 * 1000).toISOString(), message: "msg8" },
  { id: 15, sender: "Henry Cooper", channel: "Email", status: "Delivered", timestamp: new Date(Date.now() - 14 * 60 * 1000).toISOString(), message: "msg9" },
];

/** Helpers */
const hourLabel = (d) => d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

const dayLabel = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });

/**
 * Build buckets for either '24h' (hourly, 24 buckets ending at NOW)
 * or '7d' (daily, 7 buckets ending today).
 * Each bucket tracks counts per channel and total.
 */
function buildBuckets(signals, range) {
  const now = new Date();

  if (range === "24h") {
    // Buckets end at NOW so current-hour events are included.
    const buckets = [];
    for (let i = 23; i >= 0; i--) {
      const end = new Date(now.getTime() - (23 - i) * 60 * 60 * 1000); // oldest -> newest
      const start = new Date(end.getTime() - 60 * 60 * 1000);
      buckets.push({
        label: `${hourLabel(start)}-${hourLabel(end)}`,
        start,
        end,
        email: 0,
        sms: 0,
        total: 0,
      });
    }

    signals.forEach((s) => {
      const t = new Date(s.timestamp).getTime();
      for (const b of buckets) {
        if (t > b.start.getTime() && t <= b.end.getTime()) {
          if (s.channel === "Email") b.email += 1;
          else if (s.channel === "SMS") b.sms += 1;
          b.total += 1;
          break;
        }
      }
    });

    return buckets;
  }

  // 7d: daily buckets (inclusive of today), end of each bucket is 23:59:59 of that day
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const buckets = [];
  for (let i = 6; i >= 0; i--) {
    const end = new Date(endOfToday);
    end.setDate(end.getDate() - (6 - i));
    const start = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 0, 0, 0, 0);
    buckets.push({
      label: dayLabel(start), // e.g., "Jul 31"
      start,
      end,
      email: 0,
      sms: 0,
      total: 0,
    });
  }

  signals.forEach((s) => {
    const t = new Date(s.timestamp).getTime();
    for (const b of buckets) {
      if (t >= b.start.getTime() && t <= b.end.getTime()) {
        if (s.channel === "Email") b.email += 1;
        else if (s.channel === "SMS") b.sms += 1;
        b.total += 1;
        break;
      }
    }
  });

  return buckets;
}

export default function SmokeSignalDashboard() {
  const [signals] = useState(initialSignals);
  const [range, setRange] = useState("24h"); // "24h" or "7d"

  // Calculate frequency of each message
  const messageFrequency = signals.reduce((acc, signal) => {
    const key = signal.message; // Use message ID as key
    if (acc[key]) {
      acc[key] += 1;
    } else {
      acc[key] = 1;
    }
    return acc;
  }, {});

  // Define recent to get the 10 most recent signals
  const recent = useMemo(
    () =>
      [...signals]
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) // Sort by timestamp
        .slice(0, 10), // Get the most recent 10 signals
    [signals]
  );

  const metrics = useMemo(() => {
    const total = signals.length;
    const delivered = signals.filter((s) => s.status === "Delivered").length;
    const failed = signals.filter((s) => s.status === "Failed").length;
    const pending = signals.filter((s) => s.status === "Pending").length;
    const uniqueSenders = new Set(signals.map((s) => s.sender)).size;
    const emailTotal = signals.filter((s) => s.channel === "Email").length;
    const smsTotal = signals.filter((s) => s.channel === "SMS").length;

    const buckets = buildBuckets(signals, range);
    const sum = buckets.reduce((acc, b) => acc + b.total, 0);
    const avg = range === "24h" ? sum / 24 : sum / 7; // per hour vs per day

    return {
      total,
      delivered,
      failed,
      pending,
      uniqueSenders,
      emailTotal,
      smsTotal,
      avg,
      buckets,
    };
  }, [signals, range]);

  const maxCount = Math.max(...metrics.buckets.map((b) => Math.max(b.email, b.sms, b.total)), 1);
  const avgLabel = range === "24h" ? "Avg/hour" : "Avg/day";
  const subtitle =
    range === "24h"
      ? "Frequency — Messages per Hour (Last 24h)"
      : "Frequency — Messages per Day (Last 7d)";

  // Graph data for each message frequency
  const graphData = Object.keys(messageFrequency).map((msg) => ({
    message: msg,
    frequency: messageFrequency[msg],
  }));

  return (
    <div className="flex bg-black min-h-screen">
      {/* Sidebar */}
      <div className="hidden lg:block fixed top-0 left-0 h-full w-64 z-10">
        <Sidebar />
      </div>

      {/* Main */}
      <div className="flex-1 lg:ml-64 bg-black min-h-screen flex flex-col">
        <Topbar />

        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-[#ceb46a]">Smoke Signals Dashboard</h1>

            <div className="flex gap-2">
              <button
                className={`px-3 py-1 rounded border ${
                  range === "24h"
                    ? "border-[#ceb46a] text-[#ceb46a]"
                    : "border-[#2a2a2a] text-gray-300"
                }`}
                onClick={() => setRange("24h")}
              >
                Last 24h
              </button>
              <button
                className={`px-3 py-1 rounded border ${
                  range === "7d"
                    ? "border-[#ceb46a] text-[#ceb46a]"
                    : "border-[#2a2a2a] text-gray-300"
                }`}
                onClick={() => setRange("7d")}
              >
                Last 7d
              </button>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4 mb-8">
            <KPI label="Total Sent" value={metrics.total} />
            <KPI label="Delivered" value={metrics.delivered} />
            <KPI label="Failed" value={metrics.failed} />
            <KPI label="Pending" value={metrics.pending} />
            <KPI label="Unique Senders" value={metrics.uniqueSenders} />
            <KPI label="Email Total" value={metrics.emailTotal} />
            <KPI label="SMS Total" value={metrics.smsTotal} />
          </div>

          {/* Message Frequency Analysis */}
          <div className="bg-[#1c1c1e] border border-[#ceb46a] rounded p-4 sm:p-6 mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <h2 className="text-xl font-semibold text-white mb-2 sm:mb-0">Message Frequency Analysis</h2>
              <span className="text-sm text-gray-400">Total Messages Sent: {signals.length}</span>
            </div>

            {/* Horizontal Bar Chart */}
            <div className="space-y-4">
              {graphData.length > 0 ? (
                graphData
                  .sort((a, b) => b.frequency - a.frequency) // Sort by frequency (highest first)
                  .map((data, idx) => {
                    const maxFrequency = Math.max(...graphData.map(d => d.frequency));
                    const percentage = (data.frequency / maxFrequency) * 100;
                    const colorClasses = [
                      'bg-[#ceb46a]',
                      'bg-[#b9a05a]',
                      'bg-[#a48d4f]',
                      'bg-[#8f7a44]',
                      'bg-[#7a6739]',
                    ];
                    const barColor = colorClasses[idx % colorClasses.length];
                    
                    return (
                      <div key={idx} className="space-y-1">
                        {/* Label and Count Row */}
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium text-sm sm:text-base">
                              Message {data.message}
                            </span>
                            <span className="text-xs px-2 py-1 rounded-full bg-[#2a2a2a] text-gray-400">
                              #{idx + 1}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[#ceb46a] font-bold text-sm sm:text-base">
                              {data.frequency}
                            </span>
                            <span className="text-gray-400 text-xs sm:text-sm">
                              ({((data.frequency / signals.length) * 100).toFixed(1)}%)
                            </span>
                          </div>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="relative w-full bg-[#2a2a2a] rounded-full h-8 sm:h-10 overflow-hidden">
                          <div
                            className={`${barColor} h-full rounded-full transition-all duration-500 flex items-center justify-end pr-3`}
                            style={{ width: `${percentage}%` }}
                          >
                            <span className="text-black font-semibold text-xs sm:text-sm">
                              {data.frequency > 0 && percentage > 15 ? `${data.frequency} sends` : ''}
                            </span>
                          </div>
                          {percentage <= 15 && (
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white text-xs sm:text-sm font-semibold">
                              {data.frequency} sends
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
              ) : (
                <div className="text-center py-8 text-gray-400">
                  No message data available yet
                </div>
              )}
            </div>

            {/* Summary Stats */}
            <div className="mt-6 pt-6 border-t border-[#2a2a2a]">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#ceb46a]">
                    {graphData.length}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">Unique Messages</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#ceb46a]">
                    {graphData.length > 0 ? Math.max(...graphData.map(d => d.frequency)) : 0}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">Most Popular</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#ceb46a]">
                    {graphData.length > 0 ? Math.min(...graphData.map(d => d.frequency)) : 0}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">Least Used</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#ceb46a]">
                    {graphData.length > 0 ? (signals.length / graphData.length).toFixed(1) : 0}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">Avg per Message</div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Signals */}
          <div className="bg-[#1c1c1e] border border-[#ceb46a] rounded p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">Recent Signals</h2>
              <span className="text-sm text-gray-400">Last {recent.length} signals</span>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead>
                  <tr className="text-gray-300 text-sm border-b border-[#2a2a2a]">
                    <th className="py-3 pr-4 font-medium">Sender</th>
                    <th className="py-3 pr-4 font-medium">Message Type</th>
                    <th className="py-3 pr-4 font-medium">Channel</th>
                    <th className="py-3 pr-4 font-medium">Time</th>
                    <th className="py-3 pr-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((s) => {
                    const frequency = messageFrequency[s.message];
                    return (
                      <tr key={s.id} className="border-t border-[#2a2a2a] text-white hover:bg-[#2a2a2a] transition-colors">
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#ceb46a] flex items-center justify-center text-black font-bold text-sm">
                              {s.sender.charAt(0)}
                            </div>
                            <span>{s.sender}</span>
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <span className="text-[#ceb46a]">Message {s.message}</span>
                            <span className="text-xs px-2 py-1 rounded-full bg-[#2a2a2a] text-gray-400">
                              {frequency}x sent
                            </span>
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                            s.channel === 'Email' 
                              ? 'bg-blue-500/20 text-blue-400' 
                              : 'bg-green-500/20 text-green-400'
                          }`}>
                            {s.channel === 'Email' ? '📧' : '📱'} {s.channel}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-gray-300 text-sm">
                          {new Date(s.timestamp).toLocaleDateString()} at{' '}
                          {new Date(s.timestamp).toLocaleTimeString(undefined, { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </td>
                        <td className="py-3 pr-4">
                          <StatusPill status={s.status} />
                        </td>
                      </tr>
                    );
                  })}
                  {recent.length === 0 && (
                    <tr>
                      <td className="py-8 text-center text-gray-400" colSpan={5}>
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-4xl">📭</span>
                          <span>No signals sent yet</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Presentational subcomponents (no TypeScript annotations) */
function KPI({ label, value }) {
  return (
    <div className="bg-[#1c1c1e] border border-[#ceb46a] rounded p-4">
      <div className="text-gray-300 text-sm">{label}</div>
      <div className="text-2xl font-bold text-white mt-1">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const base = "text-xs px-2 py-1 rounded font-medium border";
  const cls =
    status === "Delivered"
      ? "border-[#ceb46a] text-[#ceb46a]"
      : status === "Failed"
      ? "border-red-400 text-red-400"
      : "border-yellow-400 text-yellow-400";
  return <span className={`${base} ${cls}`}>{status}</span>;
}
